import { useEffect, useState, useCallback } from 'preact/hooks'
import { lookupsApi } from '@/api'
import type { SendingDomainOptionDto } from '@/types/lookups'

// Module-level cache keyed by workspaceId — verified domains rarely change within a session.
const cache = new Map<string, SendingDomainOptionDto[]>()
const inFlight = new Map<string, Promise<SendingDomainOptionDto[] | null>>()

/**
 * Clears the cached verified-domain lookups so the next read refetches. Called after the sending
 * domains are mutated in Settings (add / verify / remove / set-default / tracking) so the activation
 * editor never pre-selects a stale default or shows a stale verified set. Clears all workspaces
 * because the management screen is company-scoped with no workspaceId in hand (mirrors the reply-to
 * lookup's invalidateReplyToAddressesLookup).
 */
export function invalidateVerifiedDomainsLookup(): void {
  cache.clear()
  inFlight.clear()
}

export interface UseVerifiedDomainsLookupResult {
  data: SendingDomainOptionDto[] | null
  isLoading: boolean
  error: string | null
  refetch: () => void
}

/** Loads the company's Verified sending domains (resolved from the workspace) as dropdown options. */
export function useVerifiedDomainsLookup(workspaceId: string | undefined): UseVerifiedDomainsLookupResult {
  const key = workspaceId || null
  const [data, setData] = useState<SendingDomainOptionDto[] | null>(key ? cache.get(key) ?? null : null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchDomains = useCallback(async (force = false) => {
    if (!workspaceId) {
      setData(null)
      return
    }
    const k = workspaceId

    if (!force && cache.has(k)) {
      setData(cache.get(k) ?? null)
      return
    }

    const existing = inFlight.get(k)
    if (existing && !force) {
      const result = await existing
      if (result) setData(result)
      return
    }

    setIsLoading(true)
    setError(null)

    const promise = lookupsApi.getVerifiedSendingDomains(workspaceId).then(response => {
      if (response.error || !response.data) {
        setError(response.error || 'Failed to load sending domains')
        return null
      }
      cache.set(k, response.data)
      return response.data
    }).finally(() => {
      inFlight.delete(k)
      setIsLoading(false)
    })

    inFlight.set(k, promise)
    const result = await promise
    if (result) setData(result)
  }, [workspaceId])

  useEffect(() => {
    void fetchDomains()
  }, [fetchDomains])

  const refetch = useCallback(() => {
    if (key) cache.delete(key)
    void fetchDomains(true)
  }, [key, fetchDomains])

  return { data, isLoading, error, refetch }
}
